import React, { useEffect, useState } from "react";
import {
    Grid,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Box,
    Typography,
    Container,
    CircularProgress,
    Alert,
    TextField,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
    getTicketTemplates,
    addTicketTemplate,
    updateTicketTemplate,
    deleteTicketTemplate,
    getActiveReaders 
} from "../../api/axios";
import { useTranslation } from "react-i18next";

const TicketTemplates = () => {
    const { t } = useTranslation("global");
    const [templates, setTemplates] = useState([]);
    const [readers, setReaders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [isReadersModalOpen, setIsReadersModalOpen] = useState(false);

    // Form state
    const [name, setName] = useState("");
    const [hFrom, setHFrom] = useState("07:00");
    const [hTo, setHTo] = useState("23:00");
    const [createTime, setCreateTime] = useState(() => {
        const today = new Date().toISOString().split('T')[0];
        return `${today} 00:00:00`;
    });
    const [dayNo, setDayNo] = useState("30");
    const [entryNo, setEntryNo] = useState("30");
    const [dailyNo, setDailyNo] = useState("0");
    const [selectedReaders, setSelectedReaders] = useState([]); // number[]

    const toUniqueNumberList = (input) => {
        const unique = (arr) => Array.from(new Set(arr));

        if (input == null) return [];

        let list = [];

        if (Array.isArray(input)) {
            list = input.map((x) => Number(x)).filter((n) => Number.isFinite(n));
            return unique(list);
        }

        if (typeof input === "string") {
            list = input
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
                .map((s) => Number(s))
                .filter((n) => Number.isFinite(n));
            return unique(list);
        }

        const one = Number(input);
        return Number.isFinite(one) ? [one] : [];
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [templatesData, readersData] = await Promise.all([
                    getTicketTemplates(),
                    getActiveReaders()
                ]);
                // Ensure templatesData is an array
                console.log('Templates data received:', templatesData);
                const templatesArray = Array.isArray(templatesData) ? templatesData : [];
                setTemplates(templatesArray);
                setReaders(readersData || []);
                if (readersData && readersData.length > 0) {
                    setSelectedReaders(toUniqueNumberList(readersData.map(reader => reader.id)));
                }
            } catch (err) {
                setError(t("ticketTemplates.errorFetching"));
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleReaderChange = (e) => {
        const value = e.target.value;

        if (value === 'all') {
            setSelectedReaders((prev) =>
                prev.length === readers.length ? [] : toUniqueNumberList(readers.map(reader => reader.id))
            );
            return;
        }

        const id = toUniqueNumberList(value)[0];
        if (!Number.isFinite(id)) return;

        setSelectedReaders((prev) =>
            prev.includes(id)
                ? prev.filter((readerId) => readerId !== id)
                : toUniqueNumberList([...prev, id])
        );
    };

    const formatTime = (time) => {
        if (time.length === 5) {
            return `${time}:00`;
        }
        return time;
    };

    const handleOpenDialog = (template = null) => {
        if (template) {
            setEditingTemplate(template);
            setName(template.name || "");
            setHFrom(template.h_from ? template.h_from.substring(0, 5) : "07:00");
            setHTo(template.h_to ? template.h_to.substring(0, 5) : "23:00");
            setCreateTime(template.create_time || "");
            setDayNo(template.day_no || "30");
            setEntryNo(template.entry_no || "30");
            setDailyNo(template.daily_no || "0");
            if (template.readers) {
                setSelectedReaders(toUniqueNumberList(template.readers));
            }
        } else {
            setEditingTemplate(null);
            resetForm();
        }
        setOpenDialog(true);
    };

    const resetForm = () => {
        setName("");
        setHFrom("07:00");
        setHTo("23:00");
        const today = new Date().toISOString().split('T')[0];
        setCreateTime(`${today} 00:00:00`);
        setDayNo("30");
        setEntryNo("30");
        setDailyNo("0");
        setSelectedReaders(toUniqueNumberList(readers.map(reader => reader.id)));
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingTemplate(null);
        resetForm();
    };

    const handleSubmit = async () => {
        if (!name.trim()) {
            setError(t("ticketTemplates.name") + " " + t("ticketTemplates.isRequired") || "Name is required");
            return;
        }

        const formattedHFrom = formatTime(hFrom);
        const formattedHTo = formatTime(hTo);
        const readersString = selectedReaders.join(',');

        const templateData = {
            name: name,
            h_from: formattedHFrom,
            h_to: formattedHTo,
            create_time: createTime,
            day_no: dayNo,
            entry_no: entryNo,
            daily_no: dailyNo,
            readers: readersString
        };

        try {
            if (editingTemplate) {
                await updateTicketTemplate(editingTemplate.id, templateData);
            } else {
                await addTicketTemplate(new URLSearchParams(templateData));
            }
            // Refresh list
            const templatesData = await getTicketTemplates();
            const templatesArray = Array.isArray(templatesData) ? templatesData : [];
            setTemplates(templatesArray);
            handleCloseDialog();
            setError(null);
        } catch (err) {
            setError(t("ticketTemplates.errorSaving"));
            console.error(err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t("ticketTemplates.deleteConfirm"))) {
            return;
        }

        try {
            await deleteTicketTemplate(id);
            const templatesData = await getTicketTemplates();
            const templatesArray = Array.isArray(templatesData) ? templatesData : [];
            setTemplates(templatesArray);
            setError(null);
        } catch (err) {
            setError(t("ticketTemplates.errorDeleting"));
            console.error(err);
        }
    };

    const getReaderNames = (readersString) => {
        if (!readersString) return "None";
        const readerIds = typeof readersString === 'string'
            ? readersString.split(',').map(r => r.trim())
            : readersString;
        const names = readerIds
            .map(id => readers.find(r => r.id.toString() === id.toString()))
            .filter(Boolean)
            .map(r => r.name);
        return names.length > 0 ? names.join(", ") : "None";
    };

    return (
        <Container maxWidth="md">
            <Box
                sx={{
                    mt: 4,
                    p: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    backgroundColor: "#f9f9f9",
                }}
            >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                    <Typography variant="h4">
                        {t("ticketTemplates.ticketTemplates")}
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                    >
                        {t("ticketTemplates.addTemplate")}
                    </Button>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {loading && <CircularProgress sx={{ display: "block", mx: "auto", mt: 2 }} />}

                {!loading && (
                    <List>
                        {!Array.isArray(templates) || templates.length === 0 ? (
                            <ListItem>
                                <ListItemText primary={t("ticketTemplates.noTemplatesFound")} />
                            </ListItem>
                        ) : (
                            Array.isArray(templates) && templates.map((template) => (
                                <ListItem key={template.id} divider>
                                    <ListItemText
                                        primary={template.name}
                                        secondary={
                                            <Box>
                                                <Typography variant="body2">
                                                    {t("ticketTemplates.time")}: {template.h_from} - {template.h_to} |
                                                    {t("ticketTemplates.days")}: {template.day_no} |
                                                    {t("ticketTemplates.entries")}: {template.entry_no} |
                                                    {t("ticketTemplates.daily")}: {template.daily_no}
                                                </Typography>
                                                <Typography variant="body2">
                                                    {t("ticketTemplates.readers")}: {getReaderNames(template.readers)}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton
                                            edge="end"
                                            aria-label="edit"
                                            onClick={() => handleOpenDialog(template)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton
                                            edge="end"
                                            aria-label="delete"
                                            onClick={() => handleDelete(template.id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))
                        )}
                    </List>
                )}

                {/* Add/Edit Dialog */}
                <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
                    <DialogTitle>
                        {editingTemplate ? t("ticketTemplates.editTemplate") : t("ticketTemplates.addTemplate")}
                    </DialogTitle>
                    <DialogContent>
                        <Grid container spacing={2} sx={{ mt: 1 }}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("ticketTemplates.name")}
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    type="time"
                                    fullWidth
                                    label={t("ticketTemplates.entryFrom")}
                                    value={hFrom}
                                    onChange={(e) => setHFrom(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    type="time"
                                    fullWidth
                                    label={t("ticketTemplates.entryUntil")}
                                    value={hTo}
                                    onChange={(e) => setHTo(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    type="datetime-local"
                                    fullWidth
                                    label={t("ticketTemplates.validFrom")}
                                    value={createTime.substring(0, 16)}
                                    onChange={(e) => setCreateTime(e.target.value + ":00")}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t("ticketTemplates.numberOfDays")}
                                    type="number"
                                    value={dayNo}
                                    onChange={(e) => setDayNo(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t("ticketTemplates.totalEntries")}
                                    type="number"
                                    value={entryNo}
                                    onChange={(e) => setEntryNo(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    fullWidth
                                    label={t("ticketTemplates.dailyEntries")}
                                    type="number"
                                    value={dailyNo}
                                    onChange={(e) => setDailyNo(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => setIsReadersModalOpen(true)}
                                >
                                    {t("ticketTemplates.selectReaders")} ({selectedReaders.length} {t("ticketTemplates.selected")})
                                </Button>
                            </Grid>
                        </Grid>

                        {/* Readers Selection Modal */}
                        <Dialog
                            open={isReadersModalOpen}
                            onClose={() => setIsReadersModalOpen(false)}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle>{t("ticketTemplates.selectReaders")}</DialogTitle>
                            <DialogContent>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={selectedReaders.length === readers.length}
                                            onChange={handleReaderChange}
                                            value="all"
                                        />
                                    }
                                    label={t("newTicket.all")}
                                />
                                <Grid container spacing={1}>
                                    {readers.map((reader) => (
                                        <Grid item xs={12} sm={6} key={reader.id}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={selectedReaders.includes(Number(reader.id))}
                                                        onChange={handleReaderChange}
                                                        value={reader.id}
                                                    />
                                                }
                                                label={reader.name}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setIsReadersModalOpen(false)}
                                    variant="contained"
                                    color="primary"
                                >
                                    OK
                                </Button>
                            </DialogActions>
                        </Dialog>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>{t("ticketTemplates.cancel")}</Button>
                        <Button onClick={handleSubmit} variant="contained" color="primary">
                            {editingTemplate ? t("ticketTemplates.update") : t("ticketTemplates.add")}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </Container>
    );
};

export default TicketTemplates;
